/**
 * Domain concepts for need/service matching.
 *
 * Each key is a canonical concept; the list holds surface forms and closely related terms that should land on
 * the same concept. Expansion is symmetric: a need mentioning any member matches a service mentioning any other.
 * Sibling concepts (accounting / tax / bookkeeping, software / web_development, ...) deliberately repeat each
 * other's core term so a pair that straddles the boundary still shares at least one concept instead of being
 * halved as "conflicting" by the lexical scorer.
 *
 * Terms are matched after normalisation and crude singularisation (see normalize.ts), so both "pipe" and
 * "pipes" are fine to list; the index stores both the raw and the singular form.
 */
export const CONCEPTS = {
    electrical: w(`electrician electrical electric wiring wire rewire rewiring outlet outlets breaker panel voltage
    lighting circuit circuits generator ev charger`),
    plumbing: w(`plumber plumbing pipe pipes drain drains leak leaking faucet toilet sink sewer water heater sump
    backflow repipe`),
    hvac: w(`hvac heating cooling furnace boiler ac airconditioning airconditioner ventilation ductwork thermostat
    heat pump refrigerant`),
    roofing: w(`roof roofer roofing shingle shingles gutter gutters flashing skylight soffit fascia`),
    carpentry: w(`carpenter carpentry woodwork woodworking cabinet cabinets framing trim molding deck decking
    millwork`),
    construction: w(`construction contractor builder building remodel remodeling renovation renovate addition drywall
    demolition masonry concrete handyman`),
    flooring: w(`floor flooring hardwood tile carpet laminate vinyl refinishing subfloor`),
    painting: w(`painter painting paint repaint interior exterior staining wallpaper drywall`),
    landscaping: w(`landscaper landscaping lawn garden gardening yard mowing irrigation sprinkler tree trees arborist
    hardscape mulch sod fence fencing`),
    cleaning: w(`cleaner cleaning maid janitorial housekeeping housekeeper deepcleaning carpet window power
    washing`),
    moving: w(`mover movers moving relocation relocating packing hauling storage freight junk removal`),
    appliance: w(`appliance appliances refrigerator washer dryer dishwasher oven stove microwave repair`),
    automotive: w(`mechanic automotive auto car cars vehicle vehicles transmission brakes engine tires bodywork
    collision detailing towing`),
    legal: w(`lawyer attorney legal law counsel litigation contract contracts paralegal estate trademark patent
    incorporation divorce custody immigration visa notary will trust`),
    accounting: w(`accountant accounting cpa audit auditing financial statements ledger payroll tax taxes bookkeeping
    bookkeeper`),
    tax: w(`tax taxes taxation irs filing return returns deduction deductions preparer cpa accountant`),
    bookkeeping: w(`bookkeeper bookkeeping ledger reconciliation invoicing invoice payroll quickbooks xero accounting
    accountant`),
    insurance: w(`insurance insurer underwriter policy coverage claims liability premium annuity`),
    real_estate: w(`realtor realestate broker property properties listing appraisal appraiser landlord tenant leasing
    rental rentals home buying selling condo`),
    mortgage: w(`mortgage lender lending loan loans refinance refinancing preapproval escrow title underwriting
    realtor`),
    financial_planning: w(`investor investment investing funding capital venture angel banking wealth portfolio
    retirement financial planner planning advisor fiduciary stocks bonds`),
    marketing: w(`marketing marketer advertising ads seo sem ppc copywriting copywriter socialmedia campaign growth
    branding leads funnel`),
    design: w(`design designer graphic branding brand logo ux ui visual illustration typography creative
    layout`),
    web_development: w(`web website webdev webdevelopment webdesign wordpress shopify ecommerce landing site sites
    frontend developer`),
    software: w(`software developer development engineer engineering programmer programming coding code app
    application backend frontend fullstack api database devops mobile ios android saas`),
    it_support: w(`helpdesk network networking server servers hardware computer computers troubleshooting laptop
    printer wifi cybersecurity msp`),
    photography: w(`photographer photography photo photos headshot headshots portrait wedding shoot`),
    video: w(`videographer video videography film filming editing editor animation motion drone youtube
    production`),
    writing: w(`writer writing editor editing proofreading copy ghostwriting technical documentation blog
    newsletter`),
    translation: w(`translator translation interpreter interpreting bilingual localization spanish french german
    chinese japanese portuguese`),
    tutoring: w(`tutor tutoring teacher teaching lesson lessons instructor instruction curriculum math science
    reading sat act homework test prep`),
    childcare: w(`childcare babysitter babysitting nanny daycare sitter preschool toddler`),
    pet_care: w(`pet pets dog dogs cat cats veterinarian vet grooming groomer boarding walking walker puppy
    kennel`),
    fitness: w(`trainer fitness gym workout coach coaching wellness yoga pilates strength running marathon`),
    nutrition: w(`nutrition nutritionist dietitian diet meal plan planning weight loss wellness`),
    therapy: w(`therapist therapy counselor counseling psychologist mental health anxiety depression couples
    grief`),
    medical: w(`doctor physician medical health healthcare nurse clinic chiropractor physical pediatrician
    dermatologist`),
    dental: w(`dentist dental orthodontist braces teeth tooth implants hygienist`),
    catering: w(`catering caterer chef cook cooking food menu buffet bartender bartending`),
    event_planning: w(`event events wedding weddings planner planning venue dj entertainment florist party
    coordinator`),
    music: w(`music musician band singer vocalist guitar piano drums lessons producer recording studio`),
    hr_recruiting: w(`recruiter recruiting hiring talent staffing headhunter placement resume interview career job
    jobs employment hr onboarding`),
    consulting: w(`consultant consulting advisory advisor strategy strategic management operations transformation
    fractional`),
    fundraising: w(`fundraising fundraiser donor donors grant grants nonprofit philanthropy capital campaign
    endowment`),
    printing: w(`printing printer print signage signs banners business cards brochures embroidery
    screenprinting`),
    security: w(`security guard surveillance alarm locksmith monitoring safety cctv cameras`),
};
function w(block) {
    return block.split(/\s+/).filter(Boolean);
}
//# sourceMappingURL=concepts.js.map